import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SiteCard from "@/components/feature/SiteCard";
import { getSiteById, navSites } from "@/mocks/navSites";
import { getCategoryById } from "@/mocks/navCategories";
import useFavorites from "@/hooks/useFavorites";

interface Props {
  siteId: string;
}

export default function SiteDetail({ siteId }: Props) {
  const site = getSiteById(siteId);
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!site) {
      document.title = "网址信息暂不可用 - 51网址导航";
      return;
    }
    document.title = `${site.name}官方网站 - 51网址导航`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      `${site.name}（${site.host}）：${site.summary} 51网址导航提供该网站的功能介绍、适用人群与同类网站推荐。`,
    );
    window.scrollTo({ top: 0 });
  }, [site]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!site) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 w-full px-4 md:px-6 py-20">
          <div className="mx-auto max-w-[560px] text-center rounded-lg bg-background-50 border border-background-200 p-10">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
              <i className="ri-error-warning-line text-2xl" />
            </span>
            <h1 className="mt-4 font-heading font-bold text-xl text-foreground-950">
              该网址信息暂不可用
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              网址可能已下线或链接有误，你可以返回首页，或者用关键词搜索其他网站。
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Link to="/search?q=%E8%A7%86%E9%A2%91" className="px-4 py-2 rounded-md bg-background-100 text-xs text-foreground-700 cursor-pointer whitespace-nowrap">
                用关键词搜索
              </Link>
              <Link to="/" className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-xs cursor-pointer whitespace-nowrap">
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = getCategoryById(site.categoryId);
  const subCategory = category?.children.find((child) => child.id === site.subCategoryId);
  const similar = navSites
    .filter(
      (item) =>
        item.id !== site.id &&
        (item.categoryId === site.categoryId ||
          item.tags.some((tag) => site.tags.includes(tag))),
    )
    .slice(0, 3);

  const favorite = isFavorite(site.id);

  const handleFavorite = () => {
    const added = toggle(site.id);
    setToast(
      added
        ? "已加入本地收藏（仅保存在当前浏览器，用于演示）"
        : "已取消收藏",
    );
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(site.url);
      setCopied(true);
      setToast("网址已复制到剪贴板");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setToast("当前浏览器不支持自动复制，请手动选择网址");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 w-full">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="px-4 md:px-6 py-4">
            <nav className="text-xs text-foreground-500 flex items-center gap-1 flex-wrap">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to={`/category/${site.categoryId}`}
                className="hover:text-primary-600 cursor-pointer"
              >
                {category?.name ?? "分类"}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">{site.name}</span>
            </nav>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 md:gap-6">
            <div className="min-w-0 space-y-4 md:space-y-5">
              <section className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-6">
                <div className="flex items-start gap-3 md:gap-4">
                  <span className="w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-lg bg-primary-500 text-background-50 font-heading font-extrabold text-xl md:text-2xl flex items-center justify-center">
                    {site.letter}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h1 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-2 flex-wrap">
                      {site.name}
                      {site.featured && (
                        <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-700 text-[11px] font-normal">
                          编辑精选
                        </span>
                      )}
                    </h1>
                    <p className="mt-1 text-sm text-foreground-500 break-all">{site.url}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line" />
                        {(site.visits / 10000).toFixed(1)} 万次访问
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        收录于 {site.addedAt}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 h-11 flex items-center justify-center gap-1.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-external-link-line text-base" />
                    访问 {site.name}
                  </a>
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`h-11 px-4 flex items-center justify-center gap-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      favorite
                        ? "bg-accent-50 text-accent-700 border border-accent-200"
                        : "bg-background-100 text-foreground-700 border border-background-200 hover:border-accent-300 hover:text-accent-600"
                    }`}
                  >
                    <i className={favorite ? "ri-star-fill" : "ri-star-line"} />
                    {favorite ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="h-11 px-4 flex items-center justify-center gap-1.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
                    {copied ? "已复制" : "复制网址"}
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-foreground-400">
                  访问按钮将在新标签页打开目标网站；收藏功能为浏览器本地存储演示。
                </p>
              </section>

              <section className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                <div className="w-full h-[200px] md:h-[340px] bg-background-100">
                  <img
                    src={site.image}
                    alt={`${site.name}网站示意图`}
                    title={`${site.name} - 51网址导航网站介绍`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-4 md:p-6">
                  <h2 className="font-heading font-bold text-base md:text-lg text-foreground-950">
                    网站介绍
                  </h2>
                  <p className="mt-3 text-sm text-foreground-700 leading-7">{site.description}</p>
                  <div className="mt-4 p-3 rounded-md bg-secondary-50 border border-secondary-100">
                    <p className="text-xs text-secondary-800 leading-6">
                      <span className="font-medium">适用人群：</span>
                      {site.audience}
                    </p>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-xs text-foreground-400 mb-2">相关分类与标签</h3>
                    <div className="flex flex-wrap gap-1.5">
                      <Link
                        to={`/category/${site.categoryId}`}
                        className="px-2.5 py-1 rounded-md bg-primary-50 text-primary-700 text-xs hover:bg-primary-100 cursor-pointer"
                      >
                        {category?.name}
                      </Link>
                      {subCategory && (
                        <Link
                          to={`/category/${site.categoryId}`}
                          className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs hover:bg-secondary-200 cursor-pointer"
                        >
                          {subCategory.name}
                        </Link>
                      )}
                      {site.tags.map((tag) => (
                        <Link
                          key={tag}
                          to={`/search?q=${encodeURIComponent(tag)}`}
                          className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-700 text-xs hover:text-primary-600 cursor-pointer"
                        >
                          # {tag}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <aside className="space-y-4">
              <div className="rounded-lg bg-background-50 border border-background-200 p-4">
                <h2 className="text-sm font-medium text-foreground-950 mb-3">网站信息</h2>
                <dl className="space-y-2.5 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <dt className="text-foreground-400">网站名称</dt>
                    <dd className="text-foreground-800 truncate">{site.name}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <dt className="text-foreground-400">所属分类</dt>
                    <dd className="text-foreground-800 truncate">{category?.name}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <dt className="text-foreground-400">域名</dt>
                    <dd className="text-foreground-800 truncate">{site.host}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <dt className="text-foreground-400">收录时间</dt>
                    <dd className="text-foreground-800">{site.addedAt}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <dt className="text-foreground-400">安全提示</dt>
                    <dd className="text-primary-700">请勿在第三方站点输入敏感信息</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg bg-background-50 border border-background-200 p-4">
                <h2 className="text-sm font-medium text-foreground-950 mb-3">猜你还想看</h2>
                <div className="space-y-1">
                  {similar.map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="group flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-background-100 cursor-pointer"
                    >
                      <span className="w-8 h-8 shrink-0 rounded-md bg-primary-50 text-primary-600 text-xs font-bold flex items-center justify-center">
                        {item.letter}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs text-foreground-900 group-hover:text-primary-600 truncate">
                          {item.name}
                        </span>
                        <span className="block text-[11px] text-foreground-400 truncate">
                          {item.summary}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-300" />
                    </Link>
                  ))}
                  {similar.length === 0 && (
                    <p className="text-xs text-foreground-400">暂无同类推荐</p>
                  )}
                </div>
              </div>

              <div className="rounded-lg bg-accent-50 border border-accent-100 p-4">
                <h2 className="text-sm font-medium text-accent-900">发现错误或想补充？</h2>
                <p className="mt-1.5 text-xs text-accent-800 leading-5">
                  域名变更、站点下线或描述有误，都可以通过反馈告诉我们。
                </p>
                <Link
                  to="/about#feedback"
                  className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-accent-500 text-background-50 text-xs hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  反馈问题
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </aside>
          </div>

          <section className="mt-8 md:mt-10">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <h2 className="font-heading font-bold text-lg text-foreground-950">
                同类网站推荐
              </h2>
              <Link
                to={`/category/${site.categoryId}`}
                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-0.5 cursor-pointer whitespace-nowrap"
              >
                查看全部 {category?.name}
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {similar.map((item) => (
                <SiteCard
                  key={item.id}
                  site={item}
                  isFavorite={isFavorite(item.id)}
                  onToggleFavorite={toggle}
                />
              ))}
            </div>
          </section>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-xs animate-pop-in">
          {toast}
        </div>
      )}

      <Footer />
    </div>
  );
}